import { CARDS } from "@reagent/content";
import type { GridSnapshot, RecipeDef } from "@reagent/sim";
import { useGameStore } from "../../app/gameStore";
import { playCue } from "../../audio/AudioEngine";
import { boundCardRecipeLabel, recipeOutputLabel, recipesForKind } from "./liveBuild";

/**
 * Spec 0068: one list of recipes, each named by its output species. Shared by
 * the install picker (Setup screen, Play tray) and the inspector's Rebind, so a
 * recipe reads the same wherever it is chosen. `lead` is the pre-selected first
 * option that sends no `recipeIds`; Rebind passes none (no unbinding, OQ7).
 */
export function RecipePicker({
  recipes,
  lead,
  selectedId,
  disabledIds,
  onPick,
}: {
  recipes: readonly RecipeDef[];
  lead?: string;
  /** The recipe id marked chosen; null marks the lead option. */
  selectedId: string | null;
  disabledIds?: ReadonlySet<string>;
  onPick: (recipeId: string | null) => void;
}) {
  return (
    <div className="setup__picker" role="radiogroup" aria-label="Recipe">
      {lead !== undefined && (
        <button
          role="radio"
          aria-checked={selectedId === null}
          className={`setup__pick${selectedId === null ? " is-selected" : ""}`}
          onClick={() => onPick(null)}
        >
          {lead}
        </button>
      )}
      {recipes.map((recipe) => (
        <button
          key={recipe.id}
          role="radio"
          aria-checked={selectedId === recipe.id}
          className={`setup__pick${selectedId === recipe.id ? " is-selected" : ""}`}
          disabled={disabledIds?.has(recipe.id) === true}
          onClick={() => onPick(recipe.id)}
        >
          {recipeOutputLabel(recipe)}
        </button>
      ))}
    </div>
  );
}

/**
 * The install picker: shown while a reactor-deploying offer is armed. The
 * pre-selected option leaves `recipeIds` off the command, so a bound card keeps
 * its own recipe and a generic one stays unbound (OQ5, OQ7).
 */
export function InstallRecipePicker({ snapshot }: { snapshot: GridSnapshot }) {
  const pendingInstall = useGameStore((s) => s.pendingInstall);
  const selectSetupOffer = useGameStore((s) => s.selectSetupOffer);
  if (!pendingInstall || CARDS[pendingInstall.cardId]?.deploysNode !== "reactor") return null;

  const { cardId } = pendingInstall;
  const own = boundCardRecipeLabel(snapshot, cardId);
  return (
    <section className="setup__section">
      <h2>Recipe</h2>
      <RecipePicker
        recipes={recipesForKind(snapshot, "reactor")}
        lead={own !== null ? `The card's recipe — ${own}` : "Any recipe (unbound)"}
        selectedId={pendingInstall.recipeIds?.[0] ?? null}
        onPick={(id) => {
          playCue("uiClick");
          selectSetupOffer(cardId, id === null ? undefined : [id]);
        }}
      />
    </section>
  );
}
